import React, { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { MainContext } from "../../contexts/MainContext";
import { imageService } from "../../services/imageService";
import "./GalleryMapper.scss";
import "./Adaptations.scss";
import Loader from "../loader-spinner/Loader";

const GalleryMapper = ({ onLoadComplete }) => {
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { allProperties, allGroups, groupProperties } = useContext(MainContext);
    const location = useLocation();
    const { cityName, communityName, propertyName } = useParams();

    // Memoize the community markers calculation so that it doesn't trigger the effect unnecessarily.
    const communityMarkers = useMemo(() => {
        const communityProperties = allProperties.filter((property) =>
            property.path?.startsWith(`/${cityName}/${communityName}/`) &&
            (property.enabled === undefined || property.enabled === true)
        );

        const communityGroupMarkersProperties = allGroups
            .map((element) =>
                element.modal_map_coords_pin.filter((property) =>
                    property.path.startsWith(
                        `/${cityName}/${communityName}/${propertyName}`
                    )
                )
            )
            .flat();

        return [...communityProperties, ...communityGroupMarkersProperties];
    }, [allProperties, allGroups, cityName, communityName, propertyName]);

    useEffect(() => {
        let isMounted = true;

        const fetchImagesForProperty = async () => {
            const currentPathWithoutGallery = location.pathname.replace(
                "/gallery",
                ""
            );
            const property = communityMarkers.find(
                (item) => item.path === currentPathWithoutGallery
            );

            if (property) {
                try {
                    let propertyId = property.id;
                    
                    // If the property doesn't have an ID directly, try to find it in groupProperties
                    if (!propertyId && groupProperties) {
                        const matchingGroupProperty = groupProperties.find(gp => 
                            gp.path === property.path || 
                            gp.name === property.name
                        );
                        
                        if (matchingGroupProperty) {
                            propertyId = matchingGroupProperty.id;
                            console.log(`Found property ID ${propertyId} for ${property.name} from groupProperties`);
                        }
                    }
                    
                    if (propertyId) {
                        // First try to fetch images from the gallery endpoint
                        try {
                            const galleryImages = await imageService.getGalleryImages(propertyId);
                            
                            if (galleryImages && galleryImages.length > 0) {
                                if (isMounted) {
                                    // Sort images by position before creating URLs
                                    const sortedImages = [...galleryImages].sort((a, b) => a.position - b.position);
                                    const imageUrls = sortedImages.map(image => 
                                        imageService.getImageUrl(image.path)
                                    );
                                    setImages(imageUrls);
                                }
                            } else {
                                // Fallback to the file-based approach
                                const galleryPath = `${cityName}/properties/properties-gallery/${propertyId}`;
                                
                                console.log('Fetching images from gallery path:', galleryPath);
                                const imageFiles = await imageService.getImages(galleryPath);

                                if (isMounted) {
                                    const imageUrls = imageFiles.map((file) =>
                                        imageService.getImageUrl(file.path)
                                    );
                                    setImages(imageUrls);
                                }
                            }
                        } catch (err) {
                            // Fallback to the file-based approach if gallery endpoint fails
                            console.log('Gallery endpoint failed, falling back to file system:', err);
                            const galleryPath = `${cityName}/properties/properties-gallery/${propertyId}`;
                            
                            console.log('Fetching images from gallery path:', galleryPath);
                            const imageFiles = await imageService.getImages(galleryPath);

                            if (isMounted) {
                                const imageUrls = imageFiles.map((file) =>
                                    imageService.getImageUrl(file.path)
                                );
                                setImages(imageUrls);
                            }
                        }
                    } else {
                        console.warn('No property ID available for property:', property.name);
                        console.log('Property details:', JSON.stringify(property, null, 2));
                        if (isMounted) {
                            setImages([]);
                            setIsLoading(false);
                            if (onLoadComplete) onLoadComplete();
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch images:", error);
                    if (isMounted) {
                        setImages([]);
                    }
                }
            }
        };

        setIsLoading(true);
        fetchImagesForProperty();

        return () => {
            isMounted = false;
        };
    }, [location.pathname, communityMarkers, cityName, groupProperties]);

    // Hide loader after images are updated
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
            if (onLoadComplete) {
                onLoadComplete();
            }
        }, 500);
        
        return () => clearTimeout(timer);
    }, [images, onLoadComplete]);

    if (images.length === 0 && isLoading) {
        return <Loader isVisible={true} />;
    }

    return (
        <div>
            {images.length > 0 ? (
                <div className="img-wrapper">
                    {images.map((src, index) => (
                        <img
                            key={index}
                            src={src}
                            alt={`Gallery ${index}`}
                            className="img"
                        />
                    ))}
                </div>
            ) : (
                <p className="error">No images available</p>
            )}
        </div>
    );
};

export default GalleryMapper;
